import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function WiltshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Wiltshire Pothole Claims | GREEN Rated, 14,758 Repairs in 2024/25 | Fixtyer"
        description="Wiltshire Council earns a GREEN DfT rating but an AMBER spend scorecard. It repaired 14,758 reported potholes in 2024/25 while surveying half its unclassified network each year. See what the data means for your claim."
        canonical="/councils/wiltshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Wiltshire', url: '/councils/wiltshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Does Wiltshire\'s GREEN rating mean my pothole claim will fail?',
            answer: 'No. GREEN means Wiltshire performs above average on network condition and best practice — it does not mean every road is defect-free. The council repaired 14,758 reported potholes in 2024/25, B and C roads in RED condition rose to 4% in 2024/25, and half of each road class is condition-surveyed on a rolling basis. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on a network-level scorecard.',
          },
          {
            question: 'What does the AMBER spend scorecard mean if Wiltshire invests extra money?',
            answer: 'The DfT Spend scorecard is AMBER despite council statements about additional highways investment. That reflects how Wiltshire\'s capital maintenance spend compares with assessed network need — not whether it fills potholes. For 2025/26 the DfT capital allocation is £32.283 million. Your claim still hinges on the specific defect, inspection records and repair history, not aggregate spend labels.',
          },
          {
            question: 'What if my pothole was on an unclassified road?',
            answer: 'Unclassified roads make up roughly 1,251 miles — about 44% of Wiltshire\'s 4,554km network. The council\'s published survey regime covers 50% of U-roads in a given year, with A-roads also surveyed on a 50% alternating-year basis. DfT road-condition returns show U-roads in RED condition at 3% in recent years — low by national standards, but that still represents dozens of kilometres needing maintenance, and a defect on your street is not disproved by a green network rating.',
          },
          {
            question: 'Wiltshire repaired 14,758 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Reported pothole repairs measure reactive work on defects the council knew about — not proof that preventative maintenance prevented the pothole that damaged your car. B/C roads in RED condition ticked up from 2% to 4% between 2020/21 and 2024/25 in DfT condition returns, and the council spent £34.9 million on pothole-related repairs in 2024/25 according to its published figures. A high repair count is evidence of ongoing defect formation, not elimination.',
          },
          {
            question: 'How does Wiltshire\'s Section 58 defence work with a GREEN rating?',
            answer: 'Wiltshire can point to GREEN condition and best-practice scorecards, SCANNER surveys, HIAMS asset records and a stated asset-management approach. That makes generic "the council doesn\'t maintain its roads" arguments weak. Your claim is stronger when you show the specific defect met intervention criteria, was reported before your damage (or should have been found on inspection), and sat on a road class with a known survey gap or rising RED-condition trend on that classification.',
          },
          {
            question: 'Does Wiltshire survey every road every year?',
            answer: 'No. Environment Select Committee minutes state the council condition-surveys 50% of principal A, C and U roads in a given year on a rolling basis. DfT U-road condition returns show 3% of unclassified roads in RED condition in recent years — a low network share, but that still represents dozens of kilometres needing maintenance, and a defect on your street is not disproved by a green network rating.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Wiltshire Council"
        councilSlug="wiltshire"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/wiltshire"
        headline="Wiltshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Wiltshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Wiltshire: Green-Rated, Still Filling Thousands of Potholes
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            DfT rates Wiltshire <strong className="text-emerald-400">GREEN</strong> overall — one of only 16 authorities
            in England at that level. Yet the Spend scorecard is <strong className="text-amber-400">AMBER</strong>, the
            council repaired <strong className="text-rose-400">14,758 reported potholes in 2024/25</strong>, and half of
            each road class is condition-surveyed on a rolling basis across 4,554km of network.
          </p>

          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-emerald-400">14,758</div>
            <div className="text-white font-medium mt-2">Reported potholes repaired in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              Despite a GREEN DfT rating, Wiltshire still ran a large reactive repair programme — and B/C roads in RED
              condition rose to 4% in the latest DfT condition return.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The DfT's Verdict — And The Amber Spend Asterisk
          </h2>
          <p className="text-surface-600 mb-8">
            Official 2025/26 scorecards from the Department for Transport
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Wiltshire Council DfT Road Maintenance Ratings 2025-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Metric</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Performance</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall Rating</td>
                  <td className="px-6 py-4 text-center text-surface-600">—</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Road Condition</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">A 79.5% · B&C 78% · U 94% good</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center text-amber-800">Capital spend score 78.3 vs assessed need</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Best Practice</td>
                  <td className="px-6 py-4 text-center text-emerald-600">Preventative spend score 100 · treatment score 57.5</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <p className="text-emerald-800">
                <strong>What GREEN means:</strong> Wiltshire is in the top tier nationally — 79.5% of A-roads, 78% of
                B&C roads and 94% of U-roads score as "good" in the DfT condition metrics that feed these ratings. The
                council publishes asset-management data, runs SCANNER surveys and has invested in preventative kit
                including Bobcat permanent-repair machines.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <p className="text-amber-800">
                <strong>What AMBER Spend means:</strong> The Spend scorecard is not a claim that Wiltshire ignores
                maintenance — the DfT capital spend score is 78.3 against assessed network need, with a preventative
                spend score of 100. AMBER reflects how capital maintenance spend compares with modelled need. For your
                claim, the Spend label matters less than whether the specific defect was inspected and repaired in time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Network size */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            4,554 Kilometres — Mostly C and U Roads
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from Wiltshire Council's published highways maintenance disclosure
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length (miles)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length (km, approx.)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium">Principal A roads</td>
                  <td className="px-6 py-4 text-center">356</td>
                  <td className="px-6 py-4 text-center">573</td>
                  <td className="px-6 py-4 text-center">12.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium">B roads</td>
                  <td className="px-6 py-4 text-center">201</td>
                  <td className="px-6 py-4 text-center">323</td>
                  <td className="px-6 py-4 text-center">7.1%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium">C roads</td>
                  <td className="px-6 py-4 text-center">1,042</td>
                  <td className="px-6 py-4 text-center">1,677</td>
                  <td className="px-6 py-4 text-center">36.6%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium">Unclassified roads</td>
                  <td className="px-6 py-4 text-center font-bold">1,251</td>
                  <td className="px-6 py-4 text-center font-bold">2,013</td>
                  <td className="px-6 py-4 text-center font-bold">43.9%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold">Total</td>
                  <td className="px-6 py-4 text-center font-bold">2,850</td>
                  <td className="px-6 py-4 text-center font-bold">4,554</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="The local highway network in Wiltshire comprises over 2,800 miles (4,554 kilometres) of road."
            source="Wiltshire Council Local Highways Maintenance Transparency Report 2025"
          />

          <p className="text-surface-600 text-sm mt-4">
            Most pothole damage claims occur on C and unclassified roads — together roughly 80% of Wiltshire's network.
            A GREEN network rating does not mean your residential street was recently condition-surveyed.
          </p>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Still Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of DfT SCANNER condition returns (red / amber / green %) submitted by Wiltshire Council
          </p>

          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">A-roads: broadly stable, slight RED uptick at the margin</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-emerald-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-100 border-b border-emerald-200">
                    <th className="px-4 py-2 text-left font-semibold text-emerald-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">69%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">66%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono text-surface-400 italic" colSpan={4}>
                      2023/24 and 2024/25 A-road RAG percentages not published in the latest DfT RDC0122 return
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (2,000km — 44% of network): RED share rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4%</td>
                    <td className="px-4 py-2 text-center font-bold">26%</td>
                    <td className="px-4 py-2 text-center font-bold">70%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C roads in RED condition doubled from 2% to 4% since 2020/21, while green B/C roads fell from 75% to
              70%. Still better than many councils — but not a zero-defect network.
            </p>
          </div>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
            <h3 className="font-semibold text-brand-900 mb-4">Unclassified roads — RED condition (DfT returns)</h3>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-4 mb-4">
              {[
                { year: '2020/21', val: '—', note: 'not reported' },
                { year: '2021/22', val: '—', note: 'not reported' },
                { year: '2022/23', val: '3%' },
                { year: '2023/24', val: '3%' },
                { year: '2024/25', val: '3%' },
              ].map((row) => (
                <div key={row.year} className="text-center">
                  <div className={`text-2xl font-bold ${row.val === '—' ? 'text-surface-400' : 'text-rose-600'}`}>{row.val}</div>
                  <div className="text-xs text-surface-500">{row.year}</div>
                  {row.note && <div className="text-xs text-surface-400 italic">{row.note}</div>}
                </div>
              ))}
            </div>
            <p className="text-surface-600 text-sm">
              At 3% RED, roughly 60km of U-roads were in the worst condition band in recent years — low nationally, but
              not zero. DfT U-road returns report RED share only; the 94% "good" figure in the scorecard is a separate
              composite metric.
            </p>
          </div>
        </div>
      </section>

      {/* Survey methodology */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 50% Rolling Survey Cycle
          </h2>
          <p className="text-surface-600 mb-8">
            How Wiltshire says it knows the condition of its roads — and where the gaps are
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 p-6 mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Condition survey coverage</th>
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Safety inspections</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                <tr>
                  <td className="px-4 py-3 font-medium">A roads</td>
                  <td className="px-4 py-3">50% of network in both directions, alternating each year</td>
                  <td className="px-4 py-3">Monthly safety inspections</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">B roads</td>
                  <td className="px-4 py-3">100% in one direction</td>
                  <td className="px-4 py-3">Monthly safety inspections</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">C roads</td>
                  <td className="px-4 py-3">50% in one direction</td>
                  <td className="px-4 py-3">Annual on residential streets</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-4 py-3 font-medium">Unclassified roads</td>
                  <td className="px-4 py-3 font-bold text-amber-800">50% surveyed per year</td>
                  <td className="px-4 py-3">Annual on residential streets</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Not Every Survey Vehicle Has SCANNER</h3>
              <p className="text-surface-600 text-sm">
                Wiltshire confirmed to its Environment Select Committee that not all vehicles carry vehicle-mounted
                lasers for SCANNER surveys — fleet replacement is under review. Condition knowledge on parts of the
                network therefore relies on visual inspection and a road condition index, not continuous laser scanning.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why This Matters For Section 58</h3>
              <p className="text-amber-800 text-sm">
                On a U-road surveyed only every other year, ask: was your road in the surveyed half? When was the last
                SCANNER or condition pass before your incident? Monthly safety inspections on A and B roads look for
                reportable defects — but the standard for "reasonable" knowledge is tighter when network-level data
                admits partial coverage across 2,013km of U-roads.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Planned interventions are based on surveyed need with works taking place in accordance with Asset Management principles after analysis of robust and consistently collected data."
            source="Wiltshire Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Reactive Reality — Thousands of Repairs Every Year
          </h2>
          <p className="text-surface-600 mb-8">
            Reported pothole repairs and reactive spend from the council's published highways data
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reported potholes repaired</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Pothole repair spend</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2022/23</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not separately published</td>
                  <td className="px-6 py-4 text-center">£18,797,627</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold">14,891</td>
                  <td className="px-6 py-4 text-center">£23,225,866</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14,758</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">£34,893,749</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">GREEN Does Not Mean Zero Potholes</h3>
              <p className="text-surface-600 text-sm">
                Wiltshire repaired nearly 15,000 reported potholes in each of the last two published years while earning
                a GREEN condition scorecard. Reactive spend rose 50% in one year (£23.2m → £34.9m). That is a council
                working hard — and a network still producing defects at scale.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Preventative Kit — Not Zero Defects</h3>
              <p className="text-emerald-800 text-sm">
                Wiltshire's ESC highways report documents preventative Bobcat surfacing in 2024/25 — permanent-repair
                machinery deployed alongside traditional pothole gangs. That supports the GREEN best-practice scorecard.
                It does not mean every defect on your road was caught before it damaged your vehicle.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following The Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            DfT allocation versus council investment — and why Spend is still AMBER
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <div className="text-3xl font-bold text-emerald-400">£32.283m</div>
              <div className="text-brand-300 text-sm mt-1">DfT capital allocation 2025/26</div>
              <div className="text-brand-400 text-xs mt-1">£23.6m baseline + £8.683m new funding</div>
            </div>
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <div className="text-3xl font-bold text-emerald-400">£20.7m</div>
              <div className="text-brand-300 text-sm mt-1">DfT Highways Maintenance fund 2024/25</div>
              <div className="text-brand-400 text-xs mt-1">Plus £5.2m Road Resurfacing Fund</div>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <div className="text-3xl font-bold text-amber-400">78.3</div>
              <div className="text-brand-200 text-sm mt-1">DfT capital spend score (vs need)</div>
              <div className="text-brand-400 text-xs mt-1">Spend scorecard: AMBER</div>
            </div>
          </div>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 p-6 mb-8">
            <h3 className="font-semibold text-white mb-4">2025/26 Planned Treatments (projected)</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <div className="text-4xl font-bold text-emerald-400">51km</div>
                <div className="text-brand-300 text-sm">Carriageway resurfacing planned</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-emerald-400">89km</div>
                <div className="text-brand-300 text-sm">Preventative surface treatments planned</div>
              </div>
            </div>
            <p className="text-brand-400 text-sm mt-4">
              On a 4,554km network, 51km of resurfacing is roughly 1.1% of the network per year — preventative
              treatment at 89km is about 2.0%. GREEN best-practice scores reflect process quality; they do not mean
              every road is rebuilt on a short cycle.
            </p>
          </div>

          <QuoteCard
            quote="All community areas have received a level of expenditure through both planned and reactive works."
            source="Wiltshire Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Balanced claim assessment */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-8">
            Claiming Against a GREEN-Rated Council
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN overall, condition and best-practice scorecards — top tier nationally
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 94% of U-roads and 78%+ of classified roads rated "good" in DfT metrics
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset-management approach, HIAMS records and SCANNER surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative Bobcat surfacing documented in 2024/25 ESC highways reporting
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence. Generic "the council doesn't maintain its roads" arguments will fail.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER Spend scorecard — capital spend below modelled need despite extra investment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C RED condition doubled to 4%; green B/C roads fell from 75% to 70% since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 50% rolling survey on A, C and U roads — half the network may lack recent condition data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> ~15,000 reported pothole repairs per year and £34.9m reactive spend in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council acknowledges expenditure varies by area — not uniform coverage everywhere
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a GREEN-rated council, your claim lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (MyWilts, FixMyStreet) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the 50% alternate-year survey gap is your strongest structural argument</li>
              <li>• Whether the defect met Wiltshire's published intervention criteria before your incident</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              Wiltshire's own published highways data where it helps you — without pretending a GREEN scorecard makes
              your pothole disappear.
            </p>
          </div>
        </div>
      </section>

      {/* Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Section 41 and Section 58 — The Legal Frame
          </h2>
          <p className="text-surface-600 mb-8">
            How Wiltshire's GREEN rating interacts with highways liability law
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Section 41 — The Duty</h3>
              <p className="text-surface-600 text-sm">
                Wiltshire must maintain highways reasonably safe for ordinary traffic. A GREEN DfT rating is evidence
                they take that duty seriously at network level. It is not a certificate that every carriageway met the
                standard on the day you hit a pothole.
              </p>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Section 58 — The Defence</h3>
              <p className="text-surface-600 text-sm">
                To defeat your claim, Wiltshire must show it had a reasonable system for inspecting and repairing the
                road. GREEN best-practice and condition scorecards strengthen that defence — but partial survey coverage,
                rising B/C RED shares and 14,758 reactive repairs in a single year all give you factual hooks to argue
                the specific defect fell through the system.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking About Your Defect</h3>
            <ul className="text-amber-800 text-sm space-y-2">
              <li>• Did the pothole meet Wiltshire's own intervention criteria before your incident?</li>
              <li>• Was it reported through MyWilts or FixMyStreet before you hit it?</li>
              <li>• Was your road in the surveyed 50% that year — or the blind half of the rolling cycle?</li>
              <li>• Was the defect reported before your incident — and if so, what does the repair record show?</li>
              <li>• Was the repair a permanent fix or a patch that failed — relevant to recurring defects?</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Wiltshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A GREEN-rated council demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800 text-left">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1">
                <li>• Submit photos and invoices</li>
                <li>• Use a generic template letter</li>
                <li>• No rolling 50% survey-gap argument</li>
                <li>• No prior MyWilts / FixMyStreet search</li>
                <li>• No B/C RED trend or spend context</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30 text-left">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1">
                <li>✅ Acknowledges GREEN rating — no baseless accusations</li>
                <li>✅ Cites 14,758 repairs and AMBER spend context</li>
                <li>✅ Argues 50% survey gap on U-roads where relevant</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Wiltshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=wiltshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Wiltshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/councils"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Councils
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Wiltshire's GREEN rating mean my pothole claim will fail?
              </h3>
              <p className="text-surface-600 text-sm">
                No. GREEN means Wiltshire performs above average on network condition and best practice — it does not
                mean every road is defect-free. The council repaired 14,758 reported potholes in 2024/25, B and C
                roads in RED condition rose to 4% in 2024/25, and half of each road class is condition-surveyed on a
                rolling basis. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably
                inspected and repaired, not on a network-level scorecard.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does the AMBER spend scorecard mean if Wiltshire invests extra money?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT Spend scorecard is AMBER despite council statements about additional highways investment. That
                reflects how Wiltshire's capital maintenance spend compares with assessed network need — not whether it
                fills potholes. For 2025/26 the DfT capital allocation is £32.283 million. Your claim still hinges on
                the specific defect, inspection records and repair history, not aggregate spend labels.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up roughly 1,251 miles — about 44% of Wiltshire's 4,554km network. The council's
                published survey regime covers 50% of U-roads in a given year, with A-roads also surveyed on a 50%
                alternating-year basis. DfT road-condition returns show U-roads in RED condition at 3% in recent years —
                low by national standards, but that still represents dozens of kilometres needing maintenance, and a
                defect on your street is not disproved by a green network rating.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Wiltshire repaired 14,758 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Reported pothole repairs measure reactive work on defects the council knew about — not proof that
                preventative maintenance prevented the pothole that damaged your car. B/C roads in RED condition ticked
                up from 2% to 4% between 2020/21 and 2024/25 in DfT condition returns, and the council spent £34.9
                million on pothole-related repairs in 2024/25 according to its published figures. A high repair count is
                evidence of ongoing defect formation, not elimination.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How does Wiltshire's Section 58 defence work with a GREEN rating?
              </h3>
              <p className="text-surface-600 text-sm">
                Wiltshire can point to GREEN condition and best-practice scorecards, SCANNER surveys, HIAMS asset
                records and a stated asset-management approach. That makes generic "the council doesn't maintain its
                roads" arguments weak. Your claim is stronger when you show the specific defect met intervention
                criteria, was reported before your damage (or should have been found on inspection), and sat on a road
                class with a known survey gap or rising RED-condition trend on that classification.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Wiltshire survey every road every year?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Environment Select Committee minutes state the council condition-surveys 50% of principal A, C and
                U roads in a given year on a rolling basis. DfT U-road condition returns show 3% of unclassified roads in
                RED condition in recent years — a low network share, but that still represents dozens of kilometres
                needing maintenance, and a defect on your street is not disproved by a green network rating.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.wiltshire.gov.uk/article/13919/Local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Wiltshire Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
